import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa"; // Install react-icons if needed
// import './Gallery.css';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Touch state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // 1. Fetch Images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(
          "https://ethiccraft.org/backend/gallery/read_isShown.php",
        );
        const imgUrls = response.data.map(
          (item) => `https://ethiccraft.org/${item.image}`,
        );
        setImages(imgUrls);
        setIsLoaded(true);
      } catch (error) {
        console.error("Failed to fetch images:", error);
      }
    };
    fetchImages();
  }, []);

  // 2. Navigation Logic
  const openLightbox = (index) => {
    setSelectedIndex(index);
    document.body.style.overflow = "hidden"; // Prevent background scrolling
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
    document.body.style.overflow = "auto"; // Restore scrolling
  };

  const showNext = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const showPrev = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // 3. Keyboard Support
  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, showNext, showPrev]);

  // 4. Touch / Swipe Support
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) showNext();
    if (isRightSwipe) showPrev();
  };

  return (
    <section className="premium-gallery-container">
      <div className="gallery-header-section">
        <h2 className="gallery-main-title">Life at Ethiccraft</h2>
        <p className="gallery-subtitle">
          A visual journey through our activities and events
        </p>
      </div>

      {/* Masonry Layout */}
      <div className={`masonry-grid ${isLoaded ? "loaded" : ""}`}>
        {images.map((img, index) => (
          <div
            key={index}
            className="masonry-item"
            onClick={() => openLightbox(index)}
          >
            <img src={img} alt={`Ethiccraft Memory ${index}`} loading="lazy" />
            <div className="item-overlay">
              <span className="view-text">View</span>
            </div>
          </div>
        ))}
      </div>

      {/* Immersive Lightbox */}
      {selectedIndex !== null && (
        <div
          className="lightbox-modal"
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button className="lightbox-btn close" onClick={closeLightbox}>
            <FaTimes />
          </button>

          <button
            className="lightbox-btn prev"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
          >
            <FaChevronLeft />
          </button>

          <div
            className="lightbox-image-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[selectedIndex]}
              alt="Full View"
              className="lightbox-full-img"
            />
            <div className="lightbox-counter">
              {selectedIndex + 1} / {images.length}
            </div>
          </div>

          <button
            className="lightbox-btn next"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
